// @greenlight-spec: 28-it-dashboard-ui.md#policies--governance-policy-set
// @greenlight-spec: 11-observability.md#storage-and-tamper-evidence
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { retentionLabel } from '@/lib/auditRetention';
import { AUDIT_RETENTION_DAY_OPTIONS, type AuditRetentionSettings } from '@greenlight/shared';

import { SettingsSectionHeading } from './SettingsSectionHeading';

const KEY = ['policies', 'audit-retention'];
const INDEFINITE = 'indefinite';

type RetentionDays = AuditRetentionSettings['retention_days'];

const toValue = (days: RetentionDays): string => (days === null ? INDEFINITE : String(days));
const fromValue = (value: string): RetentionDays =>
  value === INDEFINITE ? null : (Number(value) as Exclude<RetentionDays, null>);

/** A change that makes rows eligible for deletion that are kept today. */
const shortens = (from: RetentionDays, to: RetentionDays): boolean =>
  to !== null && (from === null || to < from);

/**
 * Settings → Policies: how long the org keeps its audit log. A daily job deletes
 * rows older than the window, so a shorter window is staged and saved
 * explicitly, with the deletion stated before it is committed.
 */
export function AuditRetentionSection(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = api.policies.getAuditRetention.useQuery(KEY, undefined, {
    queryKey: KEY,
  });
  const update = api.policies.updateAuditRetention.useMutation();

  const saved = data?.status === 200 ? data.body.retention_days : undefined;
  // undefined means no unsaved edit; null is the "keep indefinitely" choice.
  const [edit, setEdit] = React.useState<RetentionDays | undefined>(undefined);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setEdit(undefined);
  }, [saved]);

  if (isLoading) {
    return <Skeleton className="h-28 w-full" data-testid="audit-retention-loading" />;
  }
  if (isError || data?.status !== 200 || saved === undefined) {
    return (
      <Alert variant="destructive" data-testid="audit-retention-error">
        <AlertTitle>Couldn&apos;t load audit log retention</AlertTitle>
        <AlertDescription>Something went wrong fetching the retention setting.</AlertDescription>
      </Alert>
    );
  }

  const draft = edit === undefined ? saved : edit;
  const dirty = draft !== saved;
  const destructive = dirty && shortens(saved, draft);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const res = await update.mutateAsync({ body: { retention_days: draft } });
      if (res.status !== 200) throw res;
      toast.success('Audit log retention updated.');
      await queryClient.invalidateQueries({ queryKey: KEY });
      await queryClient.invalidateQueries({ queryKey: ['audit-status'] });
    } catch (err) {
      const message = (err as { body?: { message?: string } })?.body?.message;
      toast.error(message ?? "Couldn't update audit log retention.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="audit-retention">
      <SettingsSectionHeading title="Audit log retention" pill="IT" />
      <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-muted-foreground">
        How long Greenlight keeps the audit log. Entries older than this are deleted each night.
      </p>
      <div className="mt-4 rounded-gl-lg border border-border bg-card">
        <div className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={toValue(draft)} onValueChange={(value) => setEdit(fromValue(value))}>
              <SelectTrigger
                id="audit-retention-days"
                aria-label="Keep audit log for"
                className="w-full sm:w-56"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIT_RETENTION_DAY_OPTIONS.map((days) => (
                  <SelectItem key={days} value={String(days)}>
                    {retentionLabel(days)}
                  </SelectItem>
                ))}
                <SelectItem value={INDEFINITE}>{retentionLabel(null)}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant={destructive ? 'destructive' : 'default'}
              disabled={!dirty || saving}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {dirty && (
              <Button
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => setEdit(undefined)}
              >
                Cancel
              </Button>
            )}
          </div>
          {destructive && (
            <Alert variant="destructive" data-testid="audit-retention-warning">
              <AlertTitle>Older entries will be permanently deleted</AlertTitle>
              <AlertDescription>
                After you save, tonight&apos;s cleanup deletes every audit entry older than{' '}
                {retentionLabel(draft).toLowerCase()}. This can&apos;t be undone.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}
